import * as React from 'react'
import { Link } from 'react-router-dom'
import './HomeEU.css'
import Header from '../components/layouts/Header'
import Footer from '../components/layouts/Footer'
import HomeHero from '../components/sections/HomeHero'
import HeroImage from '../assets/imgs/eu/hero image.jpg'
import WalmartLogo from '../assets/imgs/eu/walmart.png'
import FederalGovernmentLogo from '../assets/imgs/eu/fedgov.png'
import NsfLogo from '../assets/imgs/eu/nsf.png'
import { ReactComponent as EmployersIcon } from '../assets/imgs/eu/employers-white.svg'
import { ReactComponent as GovernmentIcon } from '../assets/imgs/eu/gov_white.svg'
import { ReactComponent as EducationIcon } from '../assets/imgs/eu/education-white.svg'
import { ReactComponent as PartnersIcon } from '../assets/imgs/eu/delivery_partners_white.svg'
import PeopleIcon from '../assets/imgs/eu/People.svg'
import EmployersDataIcon from '../assets/imgs/eu/Employers.svg'
import EducationDataIcon from '../assets/imgs/eu/Education.svg'
import GovernmentDataIcon from '../assets/imgs/eu/Government.svg'
import LabourDataIcon from '../assets/imgs/eu/Labour-market Data.svg'
import SystemsIcon from '../assets/imgs/eu/Existing Systems.svg'
import TalentIcon from '../assets/imgs/eu/Talent Matching.svg'
import CareerIcon from '../assets/imgs/eu/Career Navigation.svg'
import SkillsIcon from '../assets/imgs/eu/Skills Intelligence.svg'
import WorkforceIcon from '../assets/imgs/eu/Workforce Intelligence.svg'
import ProgrammeIcon from '../assets/imgs/eu/Programme Insights.svg'
import CapabilitiesImage from '../assets/imgs/eu/capabilities-image.png'
import { HIDE_CONTACT_HEADER_KEY, Themes, useTheme } from '../hooks/useTheme'

const supporters = [
  [
    'Employers',
    'Recruitment, internal mobility and workforce planning.',
    EmployersIcon,
  ],
  [
    'Government & Workforce Organisations',
    'Employment support, regional workforce intelligence and programme analytics.',
    GovernmentIcon,
  ],
  [
    'Education & Research',
    'Career pathways, curriculum alignment and emerging-skills intelligence.',
    EducationIcon,
  ],
  [
    'Delivery Partners',
    'Digital capability supporting adviser-led employment and skills programmes.',
    PartnersIcon,
  ],
]

const components = [
  [
    'Pod Intelligence Core™',
    'The secure orchestration layer connecting data, platform services and workforce intelligence.',
  ],
  [
    'Pod Matching Engine™',
    'Deterministic, explainable and reproducible matching between people and opportunities.',
  ],
  [
    'Pod Atlas™',
    'A structured skills graph mapping relationships between people, occupations, industries, employers and career pathways.',
  ],
  [
    'Pod Adaptive Intelligence Engine™',
    'Personalised exploration and recommendations with transparent filtering and user control.',
  ],
]

const inputs = [
  ['People', 'Individuals and job seekers', PeopleIcon],
  ['Employers', 'Jobs, roles and workforce needs', EmployersDataIcon],
  ['Education', 'Institutions, courses and credentials', EducationDataIcon],
  ['Government', 'Programmes, policies and public data', GovernmentDataIcon],
  ['Labour-market Data', 'Market trends, skills and supply', LabourDataIcon],
  ['Existing Systems', 'ATS, HRIS, LMS, CRM and more', SystemsIcon],
]

const outputs = [
  ['Talent Matching', 'Right people. Right opportunities.', TalentIcon],
  ['Career Navigation', 'Personalised guidance and career pathways.', CareerIcon],
  ['Skills Intelligence', 'Understand skills, gaps and potential.', SkillsIcon],
  [
    'Workforce Intelligence',
    'Actionable insights for planning and decisions.',
    WorkforceIcon,
  ],
  ['Programme Insights', 'Measure impact. Improve outcomes.', ProgrammeIcon],
]

const HomeEU = () => {
  const { setTheme } = useTheme()

  React.useEffect(() => {
    setTheme(Themes.DARK)
    localStorage.setItem(HIDE_CONTACT_HEADER_KEY, 'true')
  }, [setTheme])

  return (
    <>
      <div id='smooth-wrapper'>
        <div id='smooth-content'>
          <div className='podio'>
            <main className='eu-home'>
              <HomeHero
                id='main'
                className='eu-hero'
                title='AI-powered workforce intelligence'
                description='Pod Intelligence Core™ connects people, skills, employers and labour-market data to support better career, talent and workforce decisions.'
                poweredBy={<b>Deterministic. Explainable. Secure.</b>}
                learnMoreHref='/#platform'
                credibilityLine={null}
                backgroundImage={HeroImage}
              />

              <section className='eu-section eu-intro'>
                <div className='box eu-shell'>
                  <h2 className='eu-accent-heading'>
                    Turn disconnected workforce information
                    <br />
                    into one intelligence layer
                  </h2>
                  <p>
                    Pod works alongside existing employment, HR, education and
                    workforce systems to connect people, skills, opportunities
                    and labour-market data.
                  </p>
                  <p>
                    It does not require organisations to replace their core
                    technology.
                  </p>
                  <h2>Who we support</h2>
                  <div className='skillbridge-overview-grid'>
                    {supporters.map(([title, copy, Icon]) => (
                      <article
                        key={title}
                        className='sovereign-option-card skillbridge-overview-card'
                      >
                        <div className='sovereign-option-head'>
                          <div className='skillbridge-overview-card__icon-wrap'>
                            <Icon className='sovereign-option-icon skillbridge-overview-card__icon' />
                          </div>
                        </div>
                        <h2 className='skillbridge-overview-card__title'>
                          {title}
                        </h2>
                        <p className='skillbridge-overview-card__description'>
                          {copy}
                        </p>
                      </article>
                    ))}
                  </div>
                  <div className='eu-logo-row'>
                    <img src={WalmartLogo} alt='Walmart' />
                    <img
                      src={FederalGovernmentLogo}
                      alt='US Federal Government'
                    />
                    <img src={NsfLogo} alt='National Science Foundation' />
                  </div>
                  <div className='eu-assurance'>
                    Cyber Essentials <i /> G-Cloud 15 <i /> IASME in progress{' '}
                    <i /> GDPR-aligned <i /> UK contracting entity <i />{' '}
                    London-based team
                  </div>
                </div>
              </section>

              <section className='eu-section eu-platform' id='platform'>
                <div className='box eu-shell'>
                  <h2>Platform</h2>
                  <h3 className='eu-accent-heading'>Pod Intelligence Core™</h3>
                  <p className='eu-lead'>
                    A deterministic workforce-intelligence platform connecting
                    people, skills, opportunities and labour-market data.
                  </p>
                  <div className='eu-system'>
                    <h4>Inputs: connecting people, data and opportunity</h4>
                    <div className='eu-system__list'>
                      {inputs.map(([title, copy, icon]) => (
                        <div key={title}>
                          <img src={icon} alt='' />
                          <b>{title}</b>
                          <span>{copy}</span>
                        </div>
                      ))}
                    </div>
                    <div
                      className='platform-diagram-arrow eu-system__arrow'
                      aria-hidden='true'
                    >
                      <span className='platform-diagram-arrow-line' />
                      <span className='platform-diagram-arrow-head' />
                    </div>
                    <div className='eu-system__core'>
                      Pod Intelligence Core™
                      <small>
                        Deterministic Workforce Intelligence Platform (Patent Pending)
                      </small>
                    </div>
                    <div className='eu-system__modules'>
                      <span>
                        Pod Matching Engine™
                        <small>
                          Deterministic matching of people and opportunities.
                        </small>
                      </span>
                      <span>
                        Pod Atlas™
                        <small>Skills and workforce graph mapping relationships and pathway.</small>
                      </span>
                      <span>
                        Pod Adaptive
                        <br />
                        Intelligence Engine™
                        <small>
                          AI-powered recommendations with transparency and user
                          control.
                        </small>
                      </span>
                    </div>
                    <div
                      className='platform-diagram-arrow eu-system__arrow'
                      aria-hidden='true'
                    >
                      <span className='platform-diagram-arrow-line' />
                      <span className='platform-diagram-arrow-head' />
                    </div>
                    <h4>Outputs: intelligence that improves outcomes</h4>
                    <div className='eu-system__list eu-system__list--outputs'>
                      {outputs.map(([title, copy, icon]) => (
                        <div key={title}>
                          <img src={icon} alt='' />
                          <b>{title}</b>
                          <span>{copy}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <h3 className='eu-accent-heading'>Four components</h3>
                  <div className='eu-components'>
                    {components.map(([title, copy]) => (
                      <article key={title} className='sovereign-option-card'>
                        <h3>{title}</h3>
                        <p>{copy}</p>
                      </article>
                    ))}
                  </div>
                  <div className='eu-capabilities'>
                    <img src={CapabilitiesImage} alt='Pod platform interface' />
                    <div>
                      <h3 className='eu-accent-heading'>Capabilities</h3>
                      <ul>
                        <li>Talent and opportunity matching</li>
                        <li>Career navigation</li>
                        <li>Transferable-skills analysis</li>
                        <li>Workforce intelligence</li>
                        <li>Labour-market insight</li>
                        <li>Planning and forecasting</li>
                        <li>Programme analytics</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section className='eu-section eu-public' id='public-sector'>
                <div className='box eu-shell'>
                  <h2>Public Sector</h2>
                  <div className='eu-public__grid'>
                    <div>
                      <h3 className='eu-accent-heading'>
                        Digital capability for
                        <br />
                        workforce transformation
                      </h3>
                      <p>
                        Pod provides an intelligent digital layer for
                        employment, skills and workforce programmes.
                      </p>
                      <p>
                        The platform helps participants understand transferable
                        skills, explore realistic career pathways, identify
                        relevant training and connect with employers and
                        opportunities.
                      </p>
                      <p>
                        Pod complements advisers and delivery partners rather
                        than replacing them.
                      </p>
                    </div>
                    <div className='eu-public__diagram'>
                      <div className='eu-public__node'>
                        Participants + Advisers + Employers + Training +
                        Labour-market data
                      </div>
                      <div
                        className='platform-diagram-arrow eu-public__arrow'
                        aria-hidden='true'
                      >
                        <span className='platform-diagram-arrow-line' />
                        <span className='platform-diagram-arrow-head' />
                      </div>
                      <b>Pod Intelligence Core™</b>
                      <div
                        className='platform-diagram-arrow eu-public__arrow'
                        aria-hidden='true'
                      >
                        <span className='platform-diagram-arrow-line' />
                        <span className='platform-diagram-arrow-head' />
                      </div>
                      <div className='eu-public__node'>
                        Career pathways + Skills matching + Employer connections
                        + Programme insight
                      </div>
                    </div>
                  </div>
                  <div className='eu-two-columns' id='services'>
                    <div>
                      <h3 className='eu-accent-heading'>Capabilities</h3>
                      <ul>
                        <li>Personalised career navigation</li>
                        <li>Skills translation and gap analysis</li>
                        <li>Employer and opportunity matching</li>
                        <li>Local labour-market intelligence</li>
                        <li>Participant engagement</li>
                        <li>Adviser support</li>
                        <li>Programme analytics</li>
                        <li>Outcome reporting</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className='eu-accent-heading'>Who it supports</h3>
                      <ul>
                        <li>Government departments</li>
                        <li>Local and combined authorities</li>
                        <li>Employment programme primes</li>
                        <li>Workforce agencies</li>
                        <li>Education and skills providers</li>
                        <li>Economic-development organisations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section className='eu-section eu-evidence' id='evidence'>
                <div className='box eu-shell'>
                  <h2>Evidence</h2>
                  <div className='eu-evidence__grid'>
                    <article className='sovereign-option-card'>
                      <h3>Walmart</h3>
                      <h4>Enterprise career navigation and SkillBridge</h4>
                      <p>
                        Pod has supported Walmart since 2019 through
                        skills-based career navigation and opportunity matching.
                        In 2026, Walmart expanded Pod’s role to support its
                        SkillBridge programme.
                      </p>
                    </article>
                    <article className='sovereign-option-card'>
                      <h3>US Federal Government</h3>
                      <h4>Workforce modernisation</h4>
                      <p>
                        Pod has worked with federal workforce leaders on
                        skills-based career discovery, occupational pathways and
                        public-sector workforce intelligence.
                      </p>
                    </article>
                    <article className='sovereign-option-card'>
                      <h3>National Science Foundation</h3>
                      <h4>AI Skills Observatory</h4>
                      <p>
                        Pod developed an AI Skills Observatory analysing
                        employer demand, emerging AI skills and regional
                        workforce trends, with expansion into additional states
                        and university-supply analysis.
                      </p>
                    </article>
                  </div>
                  <a
                    className='book-demo-button eu-evidence__button'
                    href='https://podglobal.com'
                    target='_blank'
                    rel='noreferrer'
                  >
                    View Pod Global case studies
                  </a>
                </div>
              </section>

              <section className='eu-section eu-trust' id='trust'>
                <div className='box eu-shell'>
                  <h2>Trust, IP & Contact</h2>
                  <div className='eu-trust__grid'>
                    <article className='eu-trust__card'>
                      <h3 className='eu-accent-heading'>Trust & Security</h3>
                      <ul>
                        <li>Cyber Essentials certified</li>
                        <li>IASME Cyber Assurance in progress</li>
                        <li>G-Cloud 15 supplier</li>
                        <li>GDPR-aligned</li>
                        <li>Cloud-hosted platform</li>
                        <li>UK contracting entity</li>
                        <li>London-based team</li>
                        <li>
                          Professional Indemnity, Public Liability and Cyber
                          Insurance
                        </li>
                      </ul>
                    </article>
                    <article className='eu-trust__card'>
                      <h3 className='eu-accent-heading'>
                        Intellectual Property
                      </h3>
                      <p>
                        Pod’s workforce-intelligence technology is supported by
                        filed US provisional patents and pending USPTO trademark
                        applications.
                      </p>
                      <div className='eu-trust__ip-groups'>
                        <div>
                          <h4>Trademarks</h4>
                          <ul>
                            <li>Pod Intelligence Core™</li>
                            <li>Pod Matching Engine™</li>
                            <li>Pod Atlas™</li>
                            <li>Pod Adaptive Intelligence Engine™</li>
                          </ul>
                        </div>
                        <div>
                          <h4>Filed provisional patents</h4>
                          <ul>
                            <li>Deterministic AI Matching Engine</li>
                            <li>
                              Cohort Dataset and Skills Graph Architecture
                            </li>
                            <li>Explainable Interface and Exploration Layer</li>
                          </ul>
                        </div>
                      </div>
                    </article>
                  </div>
                  <div id='contact' className='eu-contact-card'>
                    <div>
                      <h3 className='eu-accent-heading'>
                        Discuss a workforce
                        <br />
                        challenge with Pod Europe
                      </h3>
                    </div>
                    <p>
                      Pod Europe works with employers, government, education,
                      research and employment-programme partners.
                    </p>
                    <div>
                      <Link className='book-demo-button' to='/contact'>
                        Request a demonstration
                      </Link>
                    </div>
                  </div>
                </div>
              </section>
            </main>
            <Footer theme={Themes.DARK} isEu />
          </div>
        </div>
      </div>
      <Header isEu />
    </>
  )
}

export default HomeEU
